/**
 * File field controller.
 *
 * A file field holds a selection; a person hands it candidates. Between the two sits a policy — what
 * is accepted, how many, how large — and that policy is `fileSelectionTransition`, which already
 * existed and which every renderer wired by hand along with the state around it.
 *
 * The state around it is the part worth having here. What was **rejected** has to survive the
 * transition long enough to be shown: a field that silently drops what it will not take leaves
 * someone looking at a list missing the file they just chose, with nothing to explain it. And
 * `dragover` is a state the contract declares, so it belongs to the widget rather than to whichever
 * renderer remembered to track it.
 */
import { observerFor } from "@modyra/core";
import { clearFileSelection, fileSelectionTransition } from "../behavior.js";
import { MDY_WIDGET_CONTRACTS } from "../catalog.js";
import { MDY_I18N_MESSAGES_DEFAULT } from "../i18n.js";
import { blocksValueChange } from "../interactivity.js";
import { fieldShellRootClasses, projectFieldShellA11y } from "./shell-a11y.js";
import { engageValue, errorsVisible, holdsUneditedValue, showsAsInvalid } from "./verdict.js";
/**
 * The files in a value of any shape.
 *
 * Not a coercion: a value that is not a list holds no files, and an entry with no name is not one
 * either. What it protects is the reader — every renderer walks this list and reads a name off each
 * entry, so an entry that cannot answer takes the control off the page, and the field stops being
 * able to report the very value that is wrong with it.
 */
function filesIn(value) {
    if (!Array.isArray(value))
        return [];
    return value.filter((entry) => typeof entry?.name === "string");
}
export function createFileFieldController(options, reactivity) {
    reactivity = observerFor(options.handle, reactivity);
    const { widgetId, handle, readonly: initialReadonly = false } = options;
    const policy = {
        accept: options.accept,
        multiple: options.multiple ?? false,
        maxFileSize: options.maxFileSize,
        maxFiles: options.maxFiles,
    };
    const readonly = reactivity.signal(initialReadonly);
    const rejected = reactivity.signal([]);
    const dragover = reactivity.signal(false);
    const state = reactivity.computed(() => ({
        // A list of files, always. The model holds whatever a document put in it and reports the field
        // invalid rather than refusing the write, so this reads a value of any shape; every renderer
        // walks what it finds here and reads a name off each entry, and one handed something else throws
        // while drawing the control that was going to show the verdict.
        files: filesIn(handle.value()),
        rejected: rejected(),
        dragover: dragover(),
        // Out of play, no verdict. See verdict.ts.
        invalid: showsAsInvalid({ valid: handle.valid(), disabled: handle.disabled() }),
        disabled: handle.disabled(),
        readonly: handle.readonly() || readonly(),
        interactivity: handle.interactivity() === "enabled" && readonly()
            ? "readonly"
            : handle.interactivity(),
        required: handle.required(),
        touched: handle.touched(),
        dirty: handle.dirty(),
        pending: handle.pending(),
    }));
    const view = reactivity.computed(() => {
        const current = state();
        /** The names of what the field is holding, which the state above has vouched are files. */
        const named = current.files.map((file) => file.name);
        const shell = projectFieldShellA11y(current, handle.errors(), {
            supportingText: options.supportingText?.() ?? null,
            widgetId,
            kind: "file",
            // What is shown, not what is wrong: `aria-invalid` and the error list answer one question, and
            // a rule nobody has answered yet is not news until the person has been at the field.
            errorsVisible: errorsVisible({ disabled: current.disabled, touched: current.touched, holdsUnedited: holdsUneditedValue(current, "file") }, handle.errors()),
        });
        const definition = MDY_WIDGET_CONTRACTS.file;
        return {
            root: { classes: fieldShellRootClasses(current), attributes: {} },
            parts: {
                label: shell.label,
                control: shell.control,
                description: shell.description,
                error: shell.error,
                dropzone: {
                    classes: [
                        ...definition.parts.dropzone.classes,
                        // The state the contract declares for this part, painted from the widget rather than
                        // from whichever renderer remembered to track the drag.
                        ...(current.dragover ? ["mdy-file__dropzone--dragover"] : []),
                    ],
                    attributes: { "aria-disabled": String(current.disabled) },
                },
                // What the prompt says, and the mark on the button that empties the field. Both are things a
                // person reads, and every renderer wrote its own words for them — one wrote none, and drew
                // an empty box where the prompt goes.
                content: {
                    classes: [...definition.parts.content.classes],
                    attributes: {},
                    content: {
                        // Read defensively, because the value here is whatever the model holds — and the model
                        // is documented to *hold* a value of the wrong shape and report it invalid rather than
                        // refuse it. A projection that assumes the declared shape turns that verdict into a
                        // thrown error, and the field a person was supposed to be told about disappears.
                        text: named.length > 0 ? named.join(", ") : MDY_I18N_MESSAGES_DEFAULT.fileSelect,
                    },
                },
                clear: {
                    classes: [...definition.parts.clear.classes],
                    attributes: { "aria-label": MDY_I18N_MESSAGES_DEFAULT.fileClearSelection },
                    content: { text: "\u00d7" },
                },
                fileList: {
                    classes: [...definition.parts.fileList.classes],
                    // Announced as it changes: a file that was accepted is something that happened, and a list
                    // that updates in silence tells a screen-reader user nothing. What was *refused* never
                    // reaches this list — it is the `rejected` part that carries it.
                    attributes: { role: "list", "aria-live": "polite" },
                },
            },
        };
    });
    function dispatch(intent) {
        // A leaving is not an answer. Focus arriving and going is an act on attention: Tab is how a
        // person reads a form, and a form that treats reading as declining moves false news onto the
        // fields somebody was about to fill in. What makes this field answerable is a change to its
        // value, which `engageValue` records. ADR 0167.
        if (intent.type === "blur")
            return [];
        if (intent.type === "focus")
            return [];
        if (intent.type === "dragover") {
            // Tracked even when the field cannot take a drop: the dropzone still has to stop showing itself
            // as receptive when the pointer leaves, and a disabled field that keeps the highlight is worse
            // than one that never lit up.
            dragover.set(intent.over && !blocksValueChange(state().interactivity));
            return [];
        }
        if (blocksValueChange(state().interactivity))
            return [];
        if (intent.type === "clear") {
            const cleared = clearFileSelection();
            rejected.set([]);
            dragover.set(false);
            handle.set((cleared.value ?? []));
            engageValue(handle);
            return [{ type: "mark-dirty" }, { type: "mark-touched" }];
        }
        const next = fileSelectionTransition(intent.files, policy);
        dragover.set(false);
        // Replaced, not accumulated: what the last selection refused is what a renderer shows now, and
        // keeping the previous round's refusals would explain a file the person never offered.
        rejected.set(next.rejected);
        // `undefined` means nothing was accepted, which preserves what is held rather than clearing it.
        if (next.value === undefined)
            return [];
        handle.set((Array.isArray(next.value) ? next.value : [next.value]));
        engageValue(handle);
        // Both flags are set by `engageValue` above: one act, two flags. This decides only whether the
        // host is told about the second.
        const commands = [{ type: "mark-dirty" }];
        if (next.touched)
            commands.push({ type: "mark-touched" });
        return commands;
    }
    function setValue(files) {
        handle.set(files);
        rejected.set([]);
    }
    function setReadonly(nextReadonly) {
        readonly.set(nextReadonly);
    }
    function destroy() {
        dragover.set(false);
        rejected.set([]);
    }
    return { state, view, dispatch, setValue, setReadonly, destroy };
}
